/**
 * TDBFoodCostKPI — TDB drill-down tile showing blended food cost % across
 * all TDB cities + per-city badges.
 *
 * Data source: GET /api/tdb/ceo-kpi?month=YYYY-MM
 *
 * Source priority (handled server-side):
 *   1. tdb_food_costs (Hyperpure / vendor invoices uploaded by Aditya/Nihal)
 *   2. tdb_mis_periodic (monthly MIS Excel uploaded by Nihal)
 *
 * Month picker · default = current IST month. The "data_sources" badge
 * tells you where the numbers came from ("from invoices" / "from MIS" /
 * "no data") so you know whether to chase Nihal for an MIS upload or
 * Aditya for invoice scans.
 *
 * Levels:
 *   • Green ≤15% (target) · Amber ≤20% · Red >20% · Grey if no data
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, MapPin, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

const LEVEL_COLORS = {
  green:   { bg: '#ECFDF5', fg: '#14532D', border: '#2D8B5E' },
  amber:   { bg: '#FFFBEB', fg: '#78350F', border: '#BA7517' },
  red:     { bg: '#FEF2F2', fg: '#991B1B', border: '#D4726A' },
  no_data: { bg: '#F7F5F2', fg: '#6B7280', border: '#E5E1DC' },
};

const SOURCE_LABEL = {
  invoices: { bg: '#E8F7F4', fg: '#0F766E', label: 'from invoices' },
  mis:      { bg: '#FEF6E4', fg: '#92400E', label: 'from MIS Excel' },
  no_data:  { bg: '#F7F5F2', fg: '#6B7280', label: 'no data yet · upload MIS or invoices' },
};

function currentMonthIST() {
  // IST = UTC + 5:30. JS Date is local, so add the offset manually.
  const now = new Date(Date.now() + 5.5 * 3600 * 1000);
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

const MONTH_LABEL = (m) => {
  const [y, mm] = m.split('-');
  return new Date(Date.UTC(+y, +mm - 1, 1))
    .toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

export default function TDBFoodCostKPI({ className = '' }) {
  const [month, setMonth] = useState(() => currentMonthIST());
  const [k, setK] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback((mo) => {
    setLoading(true);
    setErr(false);
    api.get(`/tdb/ceo-kpi?month=${mo}`)
      .then(r => setK(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const sources = useMemo(() => (k?.data_sources || ['no_data']), [k]);

  if (err) return null;
  if (!k && loading) {
    return (
      <div className={`bg-white rounded-lg border p-4 ${className}`} style={{ borderColor: '#E5E1DC' }}>
        <p className="text-xs" style={{ color: '#9CA3AF' }}>Loading TDB food cost…</p>
      </div>
    );
  }
  if (!k) return null;

  const blendedLevel = LEVEL_COLORS[k.blended_level] || LEVEL_COLORS.no_data;
  const hasData = k.total_revenue > 0 || k.total_food_cost > 0;

  return (
    <div className={`bg-white rounded-lg border p-4 ${className}`} style={{ borderColor: '#E5E1DC' }}
      data-testid="tdb-food-cost-kpi">

      {/* Header · title + source pill + month picker */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Flame size={14} style={{ color: '#2A7B88' }} />
          <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>TDB · Food Cost %</h3>
          {/* Source pills */}
          <div className="flex items-center gap-1">
            {sources.map((s) => {
              const tone = SOURCE_LABEL[s] || SOURCE_LABEL.no_data;
              return (
                <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold"
                  style={{ backgroundColor: tone.bg, color: tone.fg }}
                  data-testid={`tdb-food-source-${s}`}
                  title={tone.label}>
                  {s === 'no_data' ? 'NO DATA' : s}
                </span>
              );
            })}
          </div>
        </div>
        {/* Month picker */}
        <div className="flex items-center gap-1" data-testid="tdb-food-month-picker">
          <button onClick={() => setMonth(shiftMonth(month, -1))}
            className="p-1 rounded hover:bg-[#F7F5F2]"
            title="Previous month"
            data-testid="tdb-food-month-prev">
            <ChevronLeft size={13} />
          </button>
          <span className="text-[11px] font-semibold tabular-nums px-1 min-w-[64px] text-center"
            style={{ color: '#1B2A4A' }}>
            {MONTH_LABEL(month)}
          </span>
          <button onClick={() => setMonth(shiftMonth(month, +1))}
            className="p-1 rounded hover:bg-[#F7F5F2]"
            title="Next month"
            data-testid="tdb-food-month-next">
            <ChevronRight size={13} />
          </button>
          <button onClick={() => navigate('/tdb/cost')}
            className="text-[10px] flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-[#F7F5F2] ml-1"
            style={{ color: '#185FA5' }}
            data-testid="tdb-food-drill-down">
            Drill <ArrowRight size={10} />
          </button>
        </div>
      </div>

      {/* Blended pill + summary */}
      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <div className="rounded-md px-3 py-2"
          style={{ backgroundColor: blendedLevel.bg, borderLeft: `3px solid ${blendedLevel.border}` }}>
          <p className="text-[10px] uppercase font-semibold tracking-wide" style={{ color: blendedLevel.fg }}>Blended</p>
          <p className="text-2xl font-bold leading-none mt-0.5" style={{ color: blendedLevel.fg }}>
            {hasData ? `${k.blended_food_cost_pct}%` : '—'}
          </p>
        </div>
        <div className="text-[11px] leading-tight" style={{ color: '#6B7280' }}>
          <p>Target <span className="font-semibold" style={{ color: '#1B2A4A' }}>≤{k.target_pct}%</span></p>
          {hasData && (
            <>
              <p>₹{Math.round(k.total_food_cost).toLocaleString('en-IN')} on ₹{Math.round(k.total_revenue).toLocaleString('en-IN')} revenue</p>
            </>
          )}
          {!hasData && (
            <p className="italic">No revenue or food-cost logged for this month</p>
          )}
        </div>
      </div>

      {/* Per-city tiles */}
      <div className="grid grid-cols-3 gap-2">
        {k.per_city.map(c => {
          const col = LEVEL_COLORS[c.level] || LEVEL_COLORS.no_data;
          return (
            <div key={c.city}
              className="rounded-md px-2 py-1.5 flex items-center gap-1.5"
              style={{ backgroundColor: col.bg, border: `1px solid ${col.border}` }}
              data-testid={`tdb-kpi-city-${c.city}`}>
              <MapPin size={10} style={{ color: col.fg }} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold truncate" style={{ color: col.fg }}>{c.city}</p>
                <p className="text-xs font-bold leading-none" style={{ color: col.fg }}>
                  {c.level === 'no_data' ? '—' : `${c.pct}%`}
                </p>
                {c.revenue > 0 && (
                  <p className="text-[9px] mt-0.5" style={{ color: col.fg, opacity: 0.75 }}>
                    ₹{Math.round(c.food_cost).toLocaleString('en-IN')} / ₹{Math.round(c.revenue).toLocaleString('en-IN')}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
